import Delegate from "ftdomdelegate";
import { remove, qs, add } from "@fluorescent/dom";

import { section, cart } from "@/glow";
import { on } from "@/glow/events";
import QuantityButtons from "@/lib/quantity-buttons";
import CartNoteToggle from "@/lib/cart-note-toggle";
import FreeShippingBar from "@/lib/free-shipping-bar";
import CrossSells from "@/lib/cross-sells";

const selectors = {
  cart: '[data-section-type="cart"]',
  cartError: ".cart__form-item-error",
  cartItems: "[js-cart-items]",
  cartNoteTrigger: "[data-order-note-trigger]",
  cartSubtotalWrapper: "[js-cart-footer-subtotal-wrapper]",
  cartUpdateButton: ".cart__update",
  quantityInput: ".cart .quantity-input__input",
  quantityItem: "[data-input-item]",
  freeShippingBar: "[data-free-shipping-bar]",
  crossSells: "[data-cross-sells]",
};

const classes = {
  updatingQuantity: "has-quantity-update",
  removed: "is-removed",
};

section("cart", {
  onLoad() {
    const cartNoteTrigger = qs(selectors.cartNoteTrigger, this.container);
    const freeShippingBar = qs(selectors.freeShippingBar, this.container);
    const crossSells = qs(selectors.crossSells, this.container);

    if (freeShippingBar) {
      FreeShippingBar(freeShippingBar);
    }

    if (crossSells) {
      this.crossSellsContainer = crossSells;
      this.crossSells = CrossSells(this.crossSellsContainer);
    }

    if (cartNoteTrigger) this.cartNoteToggle = CartNoteToggle(this.container);
    this.quantityButtons = QuantityButtons(this.container);

    // Events are all on events trigger by other components / functions
    this.events = [
      on("cart:updated", () => this.refreshCart()),
      on("cart:error", (_, { key, errorMessage }) => {
        this.handleErrorMessage(key, errorMessage);
      }),
      on(["quantity-update:subtract", "quantity-update:add"], (_, { key }) => {
        this.handleQuantityUpdate(key);
      }),
      on("quantity-update:remove", (_, { key }) => {
        this.handleItemRemoval(key);
      }),
    ];

    // Delegate handles all click events due to rendering different content
    // within cart
    this.delegate = new Delegate(this.container);
    this.delegate.on("change", selectors.quantityInput, e =>
      this.handleQuantityInputChange(e)
    );
  },

  refreshCart() {
    const url = `${window.theme.routes.cart.base}?section_id=${this.id}`;
    const targetCartItems = qs(selectors.cartItems, this.container);
    const targetCartSubtotalWrapper = qs(
      selectors.cartSubtotalWrapper,
      this.container
    );

    fetch(url)
      .then(response => response.text())
      .then(text => {
        const sourceDom = new DOMParser().parseFromString(text, "text/html");
        const sourceCartItems = qs(selectors.cartItems, sourceDom);
        const sourceCartSubtotalWrapper = qs(
          selectors.cartSubtotalWrapper,
          sourceDom
        );
        const sourceFreeShippingBar = qs(selectors.freeShippingBar, sourceDom);
        const sourceCrossSells = qs(selectors.crossSells, sourceDom);

        if (sourceCartItems) {
          targetCartItems.innerHTML = sourceCartItems.innerHTML;
          targetCartSubtotalWrapper.outerHTML =
            sourceCartSubtotalWrapper.outerHTML;
        } else {
          const sourceContainer = qs(selectors.cart, sourceDom);
          this.container.innerHTML = sourceContainer.innerHTML;
        }

        if (sourceFreeShippingBar) {
          const freeShippingBar = qs(selectors.freeShippingBar, this.container);

          freeShippingBar.dataset.cartTotal =
            sourceFreeShippingBar.dataset.cartTotal;

          FreeShippingBar(freeShippingBar);
        }

        if (sourceCrossSells) {
          // If the cross-sells are below the line items, the content is already replaced when the cart
          // items get updated, so we don't need to replace the content, but we need to "refresh" the
          // variable we're using to reference the container because it's been replaced.
          if (
            sourceCrossSells.getAttribute("data-position") == "below-subtotal"
          ) {
            this.crossSellsContainer.innerHTML = sourceCrossSells.innerHTML;
          } else {
            this.crossSellsContainer = qs(selectors.crossSells, this.container);
          }

          this.crossSells.unload();
          this.crossSells = CrossSells(this.crossSellsContainer);
        }
      });
  },

  handleErrorMessage(key) {
    const item = qs(`[data-key="${key}"]`, this.container);
    remove(qs(selectors.cartError, item), "hidden");
    remove(item, classes.updatingQuantity);
  },

  handleQuantityInputChange({ target }) {
    const item = target.closest(selectors.quantityItem);
    const { key } = item.dataset;

    const { currentQuantity, minQuantity, step, maxQuantity } =
      this._getQuantityValues(target);
    let newQuantity;

    if (step) {
      // round up to the nearest step. checks against minQuantity to allow for a `0` in the cart.
      const roundedQuantity = Math.ceil(currentQuantity / step) * step;
      // ensure values outside of the min/max are adjusted to be within parameters.
      newQuantity = maxQuantity
        ? Math.min(Math.max(roundedQuantity, minQuantity), maxQuantity)
        : Math.max(roundedQuantity, minQuantity);
    } else {
      newQuantity = currentQuantity;
    }

    if (isNaN(newQuantity)) {
      newQuantity = minQuantity;
    }

    cart.updateItem(key, newQuantity);
    this.handleQuantityUpdate(key);
  },

  handleQuantityUpdate(key) {
    const item = qs(`[data-key="${key}"]`, this.container);
    add(item, classes.updatingQuantity);
  },

  handleItemRemoval(key) {
    const item = qs(`[data-key="${key}"]`, this.container);
    add(item, classes.removed);
    add(item, classes.updatingQuantity);
  },

  /**
   *
   * @param {element} input - The input element to gather values and rules from.
   * @returns {{currentQuantity: int, minQuantity: int, step: int, maxQuantity: int}} - All of the values within an Object.
   */
  _getQuantityValues(input) {
    const currentQuantity = parseInt(input.value, 10);
    const minQuantity = parseInt(input.getAttribute("min"), 10);
    const step = parseInt(input.getAttribute("step"), 10);
    const maxQuantity = parseInt(input.getAttribute("max"), 10);

    return { currentQuantity, minQuantity, step, maxQuantity };
  },

  onUnload() {
    this.events.forEach(unsubscribe => unsubscribe());
    this.quantityButtons.unload();
    this.cartNoteToggle?.unload();
  },
});
